import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Download, Copy, FileText, Clock, CheckCircle, AlertCircle, Loader, Eye, EyeOff, ExternalLink } from 'lucide-react'
import { buttonVariants, componentClasses, cn } from '../../utils/designSystem'
import { formatProgress, estimateTimeRemaining, parseMarkdownContent, formatSourceToAPA, countWords } from './artikelUtils'
import { CheckCircle2 } from 'lucide-react'
import AgentHeader from '../../components/AgentHeader'

// Component for displaying sources as cards
const SourceCard = ({ source, index }) => {
  // Extract domain from URL for favicon
  const getDomainFromUrl = (url) => {
    try {
      const domain = new URL(url).hostname
      return domain.replace('www.', '')
    } catch {
      return 'unknown'
    }
  }

  const domain = getDomainFromUrl(source.url)
  const faviconUrl = `https://www.google.com/s2/favicons?domain=${domain}&sz=32`

  return (
    <div className="bg-bg-tertiary rounded-lg p-3 sm:p-4 border border-border-secondary hover:border-border-primary transition-colors min-h-[120px] sm:h-32 flex flex-col">
      <div className="flex items-start space-x-2 sm:space-x-3 mb-2 sm:mb-3">
        <div className="flex-shrink-0">
          <img 
            src={faviconUrl}
            alt={`${domain} favicon`}
            className="w-4 h-4 sm:w-5 sm:h-5 rounded"
            onError={(e) => {
              e.target.style.display = 'none'
              e.target.nextSibling.style.display = 'block'
            }}
          />
          <div className="w-4 h-4 sm:w-5 sm:h-5 bg-blue-500/10 rounded flex items-center justify-center" style={{display: 'none'}}>
            <FileText className="w-3 h-3 text-blue-500" />
          </div>
        </div>
        <div className="text-xs font-medium text-text-tertiary">
          {domain}
        </div>
      </div>
      
      <div className="flex-1 mb-2 sm:mb-3">
        <h4 className="text-sm font-semibold text-text-primary leading-tight line-clamp-2">
          {source.title}
        </h4>
      </div>
      
      <div className="mt-auto">
        <a 
          href={source.url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center text-xs text-blue-400 hover:text-blue-300 transition-colors truncate w-full"
        >
          <ExternalLink className="w-3 h-3 mr-1 flex-shrink-0" />
          <span className="truncate">Buka Sumber</span>
        </a>
      </div>
    </div>
  )
}



// Component for rendering parsed markdown content without citations
const MarkdownContent = ({ content }) => {
  const { parsedContent } = parseMarkdownContent(content) || { parsedContent: '' }
  
  // Clean content to remove references and bibliographies that might appear at the end of chapters
  const cleanContent = parsedContent
    // Remove various reference section patterns
    .replace(/\n\s*##?\s*(Referensi|Daftar Pustaka|References|Bibliography)\s*\n[\s\S]*$/gi, '')
    .replace(/\n\s*\*\*\s*(Referensi|Daftar Pustaka|References|Bibliography)\s*\*\*\s*\n[\s\S]*$/gi, '')
    // Remove numbered reference lists at the end
    .replace(/\n\s*\d+\.\s+[^\n]*\([12]\d{3}\)[\s\S]*$/g, '')
    // Remove author-year citation patterns
    .replace(/\([A-Za-z]+,?\s*[12]\d{3}\)/g, '')
    // Remove bracketed citations
    .replace(/\[\d+\]/g, '')
    .trim()
  
  const wordCount = countWords(cleanContent)
  
  return (
    <div className="space-y-6">
      {/* Main Content */}
      <div className="prose prose-invert max-w-none text-text-secondary leading-relaxed">
        <ReactMarkdown 
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({node, ...props}) => <h1 className="text-xl sm:text-2xl font-bold text-text-primary mb-3 sm:mb-4 mt-4 sm:mt-6" {...props} />,
            h2: ({node, ...props}) => <h2 className="text-lg sm:text-xl font-semibold text-text-primary mb-2 sm:mb-3 mt-4 sm:mt-5" {...props} />,
            h3: ({node, ...props}) => <h3 className="text-base sm:text-lg font-medium text-text-primary mb-2 mt-3 sm:mt-4" {...props} />,
            h4: ({node, ...props}) => <h4 className="text-sm sm:text-base font-medium text-text-primary mb-2 mt-2 sm:mt-3" {...props} />,
            p: ({node, ...props}) => <p className="mb-3 sm:mb-4 text-text-secondary leading-relaxed text-sm sm:text-base" {...props} />,
            ul: ({node, ...props}) => <ul className="list-disc ml-4 sm:ml-6 mb-3 sm:mb-4 space-y-1 sm:space-y-2 text-text-secondary text-sm sm:text-base" {...props} />,
            ol: ({node, ...props}) => <ol className="list-decimal ml-4 sm:ml-6 mb-3 sm:mb-4 space-y-1 sm:space-y-2 text-text-secondary text-sm sm:text-base" {...props} />,
            li: ({node, ...props}) => <li className="mb-1 leading-relaxed" {...props} />,
            blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-blue-500 pl-3 sm:pl-4 italic text-text-tertiary mb-3 sm:mb-4 text-sm sm:text-base" {...props} />,
            code: ({node, inline, ...props}) => 
              inline 
                ? <code className="bg-bg-tertiary text-blue-400 px-1 py-0.5 rounded text-xs sm:text-sm" {...props} />
                : <code className="block bg-bg-tertiary text-text-secondary p-2 sm:p-4 rounded-lg mb-3 sm:mb-4 overflow-x-auto text-xs sm:text-sm" {...props} />,
            strong: ({node, ...props}) => <strong className="font-semibold text-text-primary" {...props} />,
            em: ({node, ...props}) => <em className="italic text-text-secondary" {...props} />,
            a: ({node, ...props}) => <a className="text-blue-400 hover:text-blue-300 underline break-words" {...props} />,
            table: ({node, ...props}) => (
              <div className="overflow-x-auto mb-4 sm:mb-6">
                <table className="w-full border-collapse border border-border-secondary rounded-lg text-xs sm:text-sm" {...props} />
              </div>
            ),
            th: ({node, ...props}) => <th className="border border-border-secondary bg-bg-tertiary p-2 sm:p-3 text-left font-semibold text-text-primary" {...props} />,
            td: ({node, ...props}) => <td className="border border-border-secondary p-2 sm:p-3 text-text-secondary" {...props} />
          }}
        >
          {cleanContent}
        </ReactMarkdown>
      </div>
    </div>
  )
}

// Component for displaying APA formatted references
const ReferencesSection = ({ academicSources }) => {
  if (!academicSources || academicSources.length === 0) {
    return null
  }

  return (
    <div className="bg-bg-secondary rounded-xl border border-border-primary p-6 mt-8">
      <h2 className="text-xl font-semibold text-text-primary mb-6">Referensi</h2>
      <div className="space-y-4">
        {academicSources.map((source, index) => (
          <div 
            key={index} 
            className="flex items-start space-x-3"
          >
            <div className="flex-shrink-0 w-6 h-6 bg-blue-500/10 rounded-full flex items-center justify-center mt-1">
              <span className="text-xs font-medium text-blue-500">{index + 1}</span>
            </div>
            <div 
              className="flex-1 text-sm text-text-secondary leading-relaxed p-4 bg-bg-tertiary rounded-lg border border-border-secondary reference-item"
              dangerouslySetInnerHTML={{ __html: formatSourceToAPA(source, index + 1) }}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

// Component for key takeaways section
const KeyTakeawaysSection = ({ content, query, outline }) => {
  const generateKeyTakeaways = () => {
    const takeaways = []
    
    if (outline && outline.length > 0 && content) {
      // Extract key points from actual article content
      const allContent = outline
        .map(section => content[section.id])
        .filter(sectionContent => sectionContent && sectionContent.trim().length > 0)
        .join(' ')
      
      if (allContent.length > 0) {
        // Parse content to remove citations for analysis
        const { parsedContent } = parseMarkdownContent(allContent) || { parsedContent: allContent }
        const cleanContent = parsedContent
          .replace(/\([A-Za-z]+,?\s*[12]\d{3}\)/g, '')
          .replace(/\[\d+\]/g, '')
          .replace(/\n\s*##?\s*(Referensi|Daftar Pustaka|References|Bibliography)\s*\n[\s\S]*$/gi, '')
          
        // Extract sentences that seem like key points (usually longer, informative sentences)
        const sentences = cleanContent
          .split(/[.!?]+/)
          .map(s => s.trim())
          .filter(s => s.length > 50 && s.length < 200)
          .filter(s => !s.match(/^(Gambar|Tabel|Grafik|\d+\.)/))
          .slice(0, 8) // Get more to choose from
          
        if (sentences.length >= 4) {
          // Select sentences from different parts of the article
          const selectedSentences = []
          const step = Math.floor(sentences.length / 4)
          
          for (let i = 0; i < 4; i++) {
            const index = i * step
            if (sentences[index]) {
              let sentence = sentences[index].trim()
              // Clean up the sentence
              sentence = sentence.replace(/^[^\w]*/, '').replace(/[^\w]*$/, '')
              if (sentence.length > 0) {
                // Ensure sentence starts with capital letter and ends with period
                sentence = sentence.charAt(0).toUpperCase() + sentence.slice(1)
                if (!sentence.endsWith('.')) sentence += '.'
                selectedSentences.push(sentence)
              }
            }
          }
          
          if (selectedSentences.length >= 4) {
            return selectedSentences.slice(0, 4)
          }
        }
      }
    }
    
    // Fallback to generic takeaways if content analysis fails
    return [
      `Artikel "${query}" menyajikan analisis mendalam dengan struktur pembahasan yang sistematis dan terorganisir.`,
      `Pembahasan mencakup berbagai aspek penting yang saling berkaitan dalam membangun pemahaman komprehensif.`,
      `Informasi disajikan berdasarkan sintesis dari berbagai sumber akademis dan referensi terpercaya.`,
      `Artikel ini memberikan wawasan praktis yang dapat diterapkan sebagai dasar pengembangan pengetahuan lebih lanjut.`
    ]
  }

  const takeaways = generateKeyTakeaways()

  return (
    <div className="bg-bg-secondary rounded-xl border border-border-primary p-6 mt-8">
      <h2 className="text-xl font-semibold text-text-primary mb-6">Poin Penting</h2>
      <ul className="space-y-3">
        {takeaways.map((takeaway, index) => (
          <li key={index} className="flex items-start space-x-3">
            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
            <span className="text-text-secondary leading-relaxed">{takeaway}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

// Component for SEO meta description section
const MetaDescriptionSection = ({ query, wordCount, outline, content }) => {
  const generateMetaDescription = () => {
    // Create a complete SEO meta description without truncation
    const baseDescription = `Pelajari ${query} secara lengkap dan mendalam`
    
    // Create different complete descriptions based on length
    const shortDescription = `${baseDescription}. Panduan komprehensif dengan wawasan praktis dari sumber terpercaya untuk meningkatkan pemahaman Anda.`
    
    const mediumDescription = `${baseDescription} dalam artikel komprehensif ini. Dapatkan wawasan praktis dan terkini dari sumber terpercaya.`
    
    const longDescription = `${baseDescription} dalam artikel komprehensif ini. Pembahasan sistematis dengan wawasan praktis dari sumber terpercaya.`
    
    // Choose the best description that fits within SEO limits
    if (shortDescription.length <= 160) {
      return shortDescription
    } else if (mediumDescription.length <= 160) {
      return mediumDescription
    } else if (longDescription.length <= 160) {
      return longDescription
    } else {
      // Fallback: create a simple complete description
      return `Pelajari ${query} secara lengkap. Panduan komprehensif dengan wawasan praktis dan terpercaya.`
    }
  }

  const metaDescription = generateMetaDescription()

  return (
    <div className="bg-bg-secondary rounded-xl border border-border-primary p-6 mt-8">
      <h2 className="text-xl font-semibold text-text-primary mb-6">Meta Deskripsi SEO</h2>
      <div className="bg-bg-tertiary rounded-lg p-4 border border-border-secondary">
        <p className="text-text-secondary leading-relaxed text-sm">
          "{metaDescription}"
        </p>
        <div className="mt-3 text-xs text-text-tertiary">
          <div>Panjang: {metaDescription.length}/160 karakter</div>
          <div className="mt-1 text-blue-400">
            ✓ Optimal untuk SEO (mengandung kata kunci, call-to-action, dan benefit)
          </div>
        </div>
      </div>
    </div>
  )
}

const ArtikelResultPage = ({ 
  query, 
  outline, 
  generatedContent, 
  academicSources = [], 
  progress, 
  isLoading, 
  error, 
  isCompleted, 
  onBack, 
  onBackToSearch 
}) => {
  const [startTime] = useState(Date.now())
  const [copyStatus, setCopyStatus] = useState('idle')
  const [expandedSections, setExpandedSections] = useState(new Set())
  const [showAllContent, setShowAllContent] = useState(false)

  // Auto scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const progressPercentage = formatProgress(progress.current, progress.total)
  const timeRemaining = estimateTimeRemaining(progress.current, progress.total, startTime)
  
  // Calculate word statistics
  const calculateWordStats = () => {
    let totalWords = 0
    const sectionStats = []
    
    outline.forEach(section => {
      const content = generatedContent[section.id]
      const wordCount = content ? countWords(content) : 0
      
      totalWords += wordCount
      
      if (wordCount > 0) {
        sectionStats.push({
          title: section.title,
          current: wordCount
        })
      }
    })
    
    return {
      totalWords,
      sectionStats
    }
  }
  
  const wordStats = calculateWordStats()

  // Collect all citations from all sections
  const collectAllCitations = () => {
    const allCitations = []
    outline.forEach(section => {
      const content = generatedContent[section.id]
      if (content) {
        const { citations } = parseMarkdownContent(content) || { citations: [] }
        allCitations.push(...citations)
      }
    })
    return [...new Set(allCitations)].sort((a, b) => a - b)
  }

  const allCitations = collectAllCitations()

  // Generate full article content with proper rich text formatting for copy
  const generateFullContent = () => {
    // Create a temporary div to render rich text content
    const tempDiv = document.createElement('div')
    tempDiv.style.cssText = `
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roagento, sans-serif;
      line-height: 1.6;
      color: #000;
      background: #fff;
      padding: 20px;
      max-width: none;
    `
    
    // Add main title
    const mainTitle = document.createElement('h1')
    mainTitle.style.cssText = 'font-size: 28px; font-weight: bold; margin: 0 0 24px 0; color: #000;'
    mainTitle.textContent = query
    tempDiv.appendChild(mainTitle)
    
    outline.forEach(section => {
      const content = generatedContent[section.id]
      if (content && section.id !== 'referensi') { // Skip referensi section - handled separately
        // Parse content to remove citations
        const { parsedContent } = parseMarkdownContent(content) || { parsedContent: content }
        
        // Clean content to remove references and bibliographies
        const cleanedContent = parsedContent
          .replace(/\n\s*##?\s*(Referensi|Daftar Pustaka|References|Bibliography)\s*\n[\s\S]*$/gi, '')
          .replace(/\n\s*\*\*\s*(Referensi|Daftar Pustaka|References|Bibliography)\s*\*\*\s*\n[\s\S]*$/gi, '')
          .replace(/\n\s*\d+\.\s+[^\n]*\([12]\d{3}\)[\s\S]*$/g, '')
          .replace(/\([A-Za-z]+,?\s*[12]\d{3}\)/g, '')
          .replace(/\[\d+\]/g, '')
          .trim()
        
        // Add section title as H2
        const sectionTitle = document.createElement('h2')
        sectionTitle.style.cssText = 'font-size: 22px; font-weight: 600; margin: 32px 0 16px 0; color: #000;'
        sectionTitle.textContent = section.title
        tempDiv.appendChild(sectionTitle)
        
         // Process content to handle tables and other markdown elements
        let processedContent = cleanedContent
        
        // Remove table separator lines (|---|---|) first
        processedContent = processedContent.replace(/^\s*\|[-\s:]+\|\s*$/gm, '')
        
        // Remove empty lines that might be left after removing separators
        processedContent = processedContent.replace(/\n\s*\n/g, '\n')
        
        // Create a temporary container for markdown content
        const contentDiv = document.createElement('div')
        
        // Split content by lines to process each line
        const lines = processedContent.split('\n')
        let htmlContent = ''
        let inTable = false
        let tableRows = []
        
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim()
          
          // Check if this is a table row (but not a separator line)
          if (line.includes('|') && line.split('|').length > 2 && !line.match(/^\s*\|[-\s:]+\|\s*$/)) {
            if (!inTable) {
              inTable = true
              tableRows = []
            }
            const cells = line.split('|').map(cell => cell.trim()).filter(cell => cell)
            // Only add rows that have actual content (not just separators)
            if (cells.length > 0 && !cells.every(cell => cell.match(/^[-\s:]*$/))) {
              tableRows.push(cells)
            }
          } else {
            // If we were in a table and now we're not, process the table
            if (inTable && tableRows.length > 0) {
              let tableHtml = '<table style="border-collapse: collapse; width: 100%; margin: 16px 0; border: 1px solid #ddd; font-family: -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, sans-serif;">'
              tableRows.forEach((row, rowIndex) => {
                const isHeader = rowIndex === 0
                const tag = isHeader ? 'th' : 'td'
                const style = isHeader 
                  ? 'border: 1px solid #ddd; padding: 12px 16px; background: #f8f9fa; font-weight: 600; text-align: left; font-size: 14px; color: #212529;'
                  : 'border: 1px solid #ddd; padding: 12px 16px; font-size: 14px; color: #495057; vertical-align: top;'
                
                tableHtml += '<tr>'
                row.forEach(cell => {
                  // Apply basic formatting to cell content
                  let formattedCell = cell
                    .replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: 600;">$1</strong>')
                    .replace(/\*(.*?)\*/g, '<em style="font-style: italic;">$1</em>')
                    .replace(/`([^`]+)`/g, '<code style="background: #f1f3f4; padding: 2px 4px; border-radius: 3px; font-family: \'Monaco\', \'Consolas\', monospace; font-size: 13px;">$1</code>')
                  
                  tableHtml += `<${tag} style="${style}">${formattedCell}</${tag}>`
                })
                tableHtml += '</tr>'
              })
              tableHtml += '</table>'
              htmlContent += tableHtml
              inTable = false
              tableRows = []
            }
            
            // Process non-table content
            if (line) {
              // Skip only main section headings (##) that would duplicate section titles
              // But allow sub-headings (###, ####, #####, ######) to be processed
              if (line.startsWith('## ') && !line.startsWith('### ') && !line.startsWith('#### ') && !line.startsWith('##### ') && !line.startsWith('###### ')) {
                continue
              }
              
              let processedLine = line
              
              // Convert markdown to HTML
              if (line.startsWith('### ')) {
                processedLine = `<h3 style="font-size: 18px; font-weight: 500; margin: 24px 0 12px 0; color: #000;">${line.substring(4)}</h3>`
              } else if (line.startsWith('#### ')) {
                processedLine = `<h4 style="font-size: 16px; font-weight: 500; margin: 20px 0 10px 0; color: #000;">${line.substring(5)}</h4>`
              } else if (line.startsWith('##### ')) {
                processedLine = `<h5 style="font-size: 15px; font-weight: 500; margin: 18px 0 8px 0; color: #000;">${line.substring(6)}</h5>`
              } else if (line.startsWith('###### ')) {
                processedLine = `<h6 style="font-size: 14px; font-weight: 500; margin: 16px 0 6px 0; color: #000;">${line.substring(7)}</h6>`
              } else if (line.startsWith('> ')) {
                processedLine = `<blockquote style="border-left: 4px solid #ddd; padding-left: 16px; margin: 16px 0; font-style: italic; color: #666;">${line.substring(2)}</blockquote>`
              } else if (line.match(/^[-*+]\s+/)) {
                // Check if previous line was also a list item to continue the list
                const prevLine = i > 0 ? lines[i-1].trim() : ''
                const nextLine = i < lines.length - 1 ? lines[i+1].trim() : ''
                const isPrevList = prevLine.match(/^[-*+]\s+/)
                const isNextList = nextLine.match(/^[-*+]\s+/)
                
                let listItem = `<li style="margin: 4px 0; padding-left: 8px;">${line.replace(/^[-*+]\s+/, '')}</li>`
                
                if (!isPrevList) {
                  listItem = `<ul style="margin: 16px 0; padding-left: 20px; list-style-type: disc;">` + listItem
                }
                if (!isNextList) {
                  listItem = listItem + `</ul>`
                }
                
                processedLine = listItem
              } else if (line.match(/^\d+\.\s+/)) {
                // Check if previous line was also a numbered list item
                const prevLine = i > 0 ? lines[i-1].trim() : ''
                const nextLine = i < lines.length - 1 ? lines[i+1].trim() : ''
                const isPrevList = prevLine.match(/^\d+\.\s+/)
                const isNextList = nextLine.match(/^\d+\.\s+/)
                
                let listItem = `<li style="margin: 4px 0; padding-left: 8px;">${line.replace(/^\d+\.\s+/, '')}</li>`
                
                if (!isPrevList) {
                  listItem = `<ol style="margin: 16px 0; padding-left: 20px; list-style-type: decimal;">` + listItem
                }
                if (!isNextList) {
                  listItem = listItem + `</ol>`
                }
                
                processedLine = listItem
              } else {
                // Regular paragraph
                processedLine = `<p style="margin: 16px 0; line-height: 1.6;">${line}</p>`
              }
              
              // Apply inline formatting
              processedLine = processedLine
                .replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: 600;">$1</strong>')
                .replace(/\*(.*?)\*/g, '<em style="font-style: italic;">$1</em>')
                .replace(/`([^`]+)`/g, '<code style="background: #f0f0f0; padding: 2px 4px; border-radius: 3px; font-family: \'Monaco\', \'Consolas\', monospace; font-size: 14px;">$1</code>')
                .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color: #0066cc; text-decoration: underline;">$1</a>')
              
              htmlContent += processedLine
            } else {
              htmlContent += '<br>'
            }
          }
        }
        
        // Handle any remaining table at the end
        if (inTable && tableRows.length > 0) {
          let tableHtml = '<table style="border-collapse: collapse; width: 100%; margin: 16px 0; border: 1px solid #ddd; font-family: -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, sans-serif;">'
          tableRows.forEach((row, rowIndex) => {
            const isHeader = rowIndex === 0
            const tag = isHeader ? 'th' : 'td'
            const style = isHeader 
              ? 'border: 1px solid #ddd; padding: 12px 16px; background: #f8f9fa; font-weight: 600; text-align: left; font-size: 14px; color: #212529;'
              : 'border: 1px solid #ddd; padding: 12px 16px; font-size: 14px; color: #495057; vertical-align: top;'
            
            tableHtml += '<tr>'
            row.forEach(cell => {
              // Apply basic formatting to cell content
              let formattedCell = cell
                .replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: 600;">$1</strong>')
                .replace(/\*(.*?)\*/g, '<em style="font-style: italic;">$1</em>')
                .replace(/`([^`]+)`/g, '<code style="background: #f1f3f4; padding: 2px 4px; border-radius: 3px; font-family: \'Monaco\', \'Consolas\', monospace; font-size: 13px;">$1</code>')
              
              tableHtml += `<${tag} style="${style}">${formattedCell}</${tag}>`
            })
            tableHtml += '</tr>'
          })
          tableHtml += '</table>'
          htmlContent += tableHtml
        }
        
        // Handle code blocks
        htmlContent = htmlContent.replace(/```[\s\S]*?```/g, (match) => {
          const code = match.replace(/```\w*\n?/g, '').replace(/```$/g, '')
          return `<pre style="background: #f5f5f5; padding: 12px; border-radius: 4px; margin: 16px 0; font-family: 'Monaco', 'Consolas', monospace; font-size: 14px; overflow-x: auto; white-space: pre-wrap;">${code}</pre>`
        })
        
        // List items are now properly wrapped in ul/ol tags during processing
        
        contentDiv.innerHTML = htmlContent
        tempDiv.appendChild(contentDiv)
      }
    })
    
    // Add references section
    if (academicSources && academicSources.length > 0) {
      const referencesTitle = document.createElement('h2')
      referencesTitle.style.cssText = 'font-size: 22px; font-weight: 600; margin: 32px 0 16px 0; color: #000;'
      referencesTitle.textContent = 'Referensi'
      tempDiv.appendChild(referencesTitle)
      
      const referencesList = document.createElement('ol')
      referencesList.style.cssText = 'margin: 16px 0; padding-left: 20px; list-style-type: decimal;'
      
      academicSources.forEach((source, index) => {
        const listItem = document.createElement('li')
        listItem.style.cssText = 'margin: 12px 0; line-height: 1.6;'
        listItem.innerHTML = formatSourceToAPA(source, index + 1)
        referencesList.appendChild(listItem)
      })
      
      tempDiv.appendChild(referencesList)
    }
    
    // Add key takeaways section
    const takeawaysTitle = document.createElement('h2')
    takeawaysTitle.style.cssText = 'font-size: 22px; font-weight: 600; margin: 32px 0 16px 0; color: #000;'
    takeawaysTitle.textContent = 'Poin Penting'
    tempDiv.appendChild(takeawaysTitle)
    
    const takeawaysList = document.createElement('ul')
    takeawaysList.style.cssText = 'margin: 16px 0; padding-left: 20px; list-style-type: disc;'
    
    // Generate takeaways
    const generateTakeaways = () => {
      const takeaways = []
      
      if (outline && outline.length > 0 && generatedContent) {
        // Extract key points from actual article content (exclude references)
        const allContent = outline
          .filter(section => section.id !== 'referensi')
          .map(section => generatedContent[section.id])
          .filter(sectionContent => sectionContent && sectionContent.trim().length > 0)
          .join(' ')
        
        if (allContent.length > 0) {
          // Parse content to remove citations for analysis
          const { parsedContent } = parseMarkdownContent(allContent) || { parsedContent: allContent }
          const cleanContent = parsedContent
            .replace(/\([A-Za-z]+,?\s*[12]\d{3}\)/g, '')
            .replace(/\[\d+\]/g, '')
            .replace(/\n\s*##?\s*(Referensi|Daftar Pustaka|References|Bibliography)\s*\n[\s\S]*$/gi, '')
            
          // Extract sentences that seem like key points (usually longer, informative sentences)
          const sentences = cleanContent
            .split(/[.!?]+/)
            .map(s => s.trim())
            .filter(s => s.length > 50 && s.length < 200)
            .filter(s => !s.match(/^(Gambar|Tabel|Grafik|\d+\.)/))
            .slice(0, 8) // Get more to choose from
            
          if (sentences.length >= 4) {
            // Select sentences from different parts of the article
            const selectedSentences = []
            const step = Math.floor(sentences.length / 4)
            
            for (let i = 0; i < 4; i++) {
              const index = i * step
              if (sentences[index]) {
                let sentence = sentences[index].trim()
                // Clean up the sentence
                sentence = sentence.replace(/^[^\w]*/, '').replace(/[^\w]*$/, '')
                if (sentence.length > 0) {
                  // Ensure sentence starts with capital letter and ends with period
                  sentence = sentence.charAt(0).toUpperCase() + sentence.slice(1)
                  if (!sentence.endsWith('.')) sentence += '.'
                  selectedSentences.push(sentence)
                }
              }
            }
            
            if (selectedSentences.length >= 4) {
              return selectedSentences.slice(0, 4)
            }
          }
        }
      }
      
      // Fallback to generic takeaways if content analysis fails
      return [
        `Artikel "${query}" menyajikan analisis mendalam dengan struktur pembahasan yang sistematis dan terorganisir.`,
        `Pembahasan mencakup berbagai aspek penting yang saling berkaitan dalam membangun pemahaman komprehensif.`,
        `Informasi disajikan berdasarkan sintesis dari berbagai sumber akademis dan referensi terpercaya.`,
        `Artikel ini memberikan wawasan praktis yang dapat diterapkan sebagai dasar pengembangan pengetahuan lebih lanjut.`
      ]
    }
    
    const takeaways = generateTakeaways()
    takeaways.forEach(takeaway => {
      const listItem = document.createElement('li')
      listItem.style.cssText = 'margin: 8px 0; line-height: 1.6;'
      listItem.textContent = takeaway
      takeawaysList.appendChild(listItem)
    })
    tempDiv.appendChild(takeawaysList)

         // Add meta description section
     const metaDescriptionTitle = document.createElement('h2')
     metaDescriptionTitle.style.cssText = 'font-size: 22px; font-weight: 600; margin: 32px 0 16px 0; color: #000;'
     metaDescriptionTitle.textContent = 'Meta Deskripsi SEO'
     tempDiv.appendChild(metaDescriptionTitle)

     // Use the same function as MetaDescriptionSection for consistency
     const generateCopyMetaDescription = () => {
       // Create a complete SEO meta description without truncation
       const baseDescription = `Pelajari ${query} secara lengkap dan mendalam`
       
       // Create different complete descriptions based on length
       const shortDescription = `${baseDescription}. Panduan komprehensif dengan wawasan praktis dari sumber terpercaya untuk meningkatkan pemahaman Anda.`
       
       const mediumDescription = `${baseDescription} dalam artikel komprehensif ini. Dapatkan wawasan praktis dan terkini dari sumber terpercaya.`
       
       const longDescription = `${baseDescription} dalam artikel komprehensif ini. Pembahasan sistematis dengan wawasan praktis dari sumber terpercaya.`
       
       // Choose the best description that fits within SEO limits
       if (shortDescription.length <= 160) {
         return shortDescription
       } else if (mediumDescription.length <= 160) {
         return mediumDescription
       } else if (longDescription.length <= 160) {
         return longDescription
       } else {
         // Fallback: create a simple complete description
         return `Pelajari ${query} secara lengkap. Panduan komprehensif dengan wawasan praktis dan terpercaya.`
       }
     }

     const metaDescriptionContent = document.createElement('div')
     metaDescriptionContent.style.cssText = 'margin: 16px 0; line-height: 1.6;'
     metaDescriptionContent.innerHTML = `<p style="line-height: 1.6;">${generateCopyMetaDescription()}</p>`
     tempDiv.appendChild(metaDescriptionContent)
    
    // Append to body temporarily to copy rich text
    document.body.appendChild(tempDiv)
    
    // Select and copy the content
    const range = document.createRange()
    range.selectNodeContents(tempDiv)
    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(range)
    
    let success = false
    try {
      success = document.execCommand('copy')
    } catch (err) {
      console.error('Copy failed:', err)
    }
    
    // Clean up
    selection.removeAllRanges()
    document.body.removeChild(tempDiv)
    
    // Return plain text as fallback
    return tempDiv.textContent || tempDiv.innerText || ''
  }

  // Copy to clipboard
  const handleCopy = async () => {
    try {
      generateFullContent() // This function now handles the copy operation directly
      setCopyStatus('success')
      setTimeout(() => setCopyStatus('idle'), 2000)
    } catch (err) {
      setCopyStatus('error')
      setTimeout(() => setCopyStatus('idle'), 2000)
    }
  }

  // Generate plain text content for download
  const generatePlainTextContent = () => {
    let fullContent = `# ${query}\n\n`
    
    outline.forEach(section => {
      const content = generatedContent[section.id]
      if (content && section.id !== 'referensi') { // Skip referensi section - handled separately
        const { parsedContent } = parseMarkdownContent(content) || { parsedContent: content }
        
        // Clean content to remove references and bibliographies
        const cleanedContent = parsedContent
          .replace(/\n\s*##?\s*(Referensi|Daftar Pustaka|References|Bibliography)\s*\n[\s\S]*$/gi, '')
          .replace(/\n\s*\*\*\s*(Referensi|Daftar Pustaka|References|Bibliography)\s*\*\*\s*\n[\s\S]*$/gi, '')
          .replace(/\n\s*\d+\.\s+[^\n]*\([12]\d{3}\)[\s\S]*$/g, '')
          .replace(/\([A-Za-z]+,?\s*[12]\d{3}\)/g, '')
          .replace(/\[\d+\]/g, '')
          .trim()
        
        fullContent += `## ${section.title}\n\n`
        fullContent += cleanedContent + '\n\n'
      }
    })
    
    // Add references
    if (academicSources && academicSources.length > 0) {
      fullContent += `## Referensi\n\n`
      academicSources.forEach((source, index) => {
        // Convert HTML to plain text for download
        const plainTextAPA = formatSourceToAPA(source, index + 1)
          .replace(/<em>/g, '')
          .replace(/<\/em>/g, '')
          .replace(/<a[^>]*>/g, '')
          .replace(/<\/a>/g, '')
        fullContent += `${index + 1}. ${plainTextAPA}\n\n`
      })
    }
    
    // Add key takeaways
    fullContent += `## Poin Penting\n\n`
    const generateTakeaways = () => {
      const takeaways = []
      
      if (outline && outline.length > 0 && generatedContent) {
        // Extract key points from actual article content (exclude references)
        const allContent = outline
          .filter(section => section.id !== 'referensi')
          .map(section => generatedContent[section.id])
          .filter(sectionContent => sectionContent && sectionContent.trim().length > 0)
          .join(' ')
        
        if (allContent.length > 0) {
          // Parse content to remove citations for analysis
          const { parsedContent } = parseMarkdownContent(allContent) || { parsedContent: allContent }
          const cleanContent = parsedContent
            .replace(/\([A-Za-z]+,?\s*[12]\d{3}\)/g, '')
            .replace(/\[\d+\]/g, '')
            .replace(/\n\s*##?\s*(Referensi|Daftar Pustaka|References|Bibliography)\s*\n[\s\S]*$/gi, '')
            
          // Extract sentences that seem like key points (usually longer, informative sentences)
          const sentences = cleanContent
            .split(/[.!?]+/)
            .map(s => s.trim())
            .filter(s => s.length > 50 && s.length < 200)
            .filter(s => !s.match(/^(Gambar|Tabel|Grafik|\d+\.)/))
            .slice(0, 8) // Get more to choose from
            
          if (sentences.length >= 4) {
            // Select sentences from different parts of the article
            const selectedSentences = []
            const step = Math.floor(sentences.length / 4)
            
            for (let i = 0; i < 4; i++) {
              const index = i * step
              if (sentences[index]) {
                let sentence = sentences[index].trim()
                // Clean up the sentence
                sentence = sentence.replace(/^[^\w]*/, '').replace(/[^\w]*$/, '')
                if (sentence.length > 0) {
                  // Ensure sentence starts with capital letter and ends with period
                  sentence = sentence.charAt(0).toUpperCase() + sentence.slice(1)
                  if (!sentence.endsWith('.')) sentence += '.'
                  selectedSentences.push(sentence)
                }
              }
            }
            
            if (selectedSentences.length >= 4) {
              return selectedSentences.slice(0, 4)
            }
          }
        }
      }
      
      // Fallback to generic takeaways if content analysis fails
      return [
        `Artikel "${query}" menyajikan analisis mendalam dengan struktur pembahasan yang sistematis dan terorganisir.`,
        `Pembahasan mencakup berbagai aspek penting yang saling berkaitan dalam membangun pemahaman komprehensif.`,
        `Informasi disajikan berdasarkan sintesis dari berbagai sumber akademis dan referensi terpercaya.`,
        `Artikel ini memberikan wawasan praktis yang dapat diterapkan sebagai dasar pengembangan pengetahuan lebih lanjut.`
      ]
    }
    
    const takeaways = generateTakeaways()
    takeaways.forEach((takeaway, index) => {
      fullContent += `${index + 1}. ${takeaway}\n`
    })
    fullContent += `\n`
    
    // Add meta description - use same function as UI for consistency
    const generateMetaDescription = () => {
      // Create a proper SEO meta description like real articles
      let description = `Pelajari ${query} secara lengkap dalam artikel komprehensif ini`
      
      // Extract key benefits or main points from outline
      if (outline && outline.length > 0) {
        const mainSections = outline
          .filter(section => !['pendahuluan', 'kesimpulan', 'referensi'].includes(section.id.toLowerCase()))
          .slice(0, 2) // Take first 2 main sections
        
        if (mainSections.length > 0) {
          const topics = mainSections.map(section => section.title.toLowerCase()).join(' dan ')
          description += `. Pembahasan mendalam tentang ${topics}`
        }
      }
      
      description += `. Dapatkan wawasan praktis dan terkini dari sumber terpercaya untuk meningkatkan pemahaman Anda.`
      
      // Ensure meta description is within SEO limits (150-160 characters)
      if (description.length > 160) {
        // Try to cut at a word boundary near 157 characters
        const truncated = description.substring(0, 157)
        const lastSpace = truncated.lastIndexOf(' ')
        return truncated.substring(0, lastSpace) + '...'
      }
      
      return description
    }
    
    fullContent += `## Meta Deskripsi SEO\n\n`
    fullContent += `${generateMetaDescription()}\n\n`
    
    return fullContent.trim()
  }

  // Download as markdown
  const handleDownload = () => {
    const fullContent = generatePlainTextContent()
    const blob = new Blob([fullContent], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `artikel-${query.toLowerCase().replace(/\s+/g, '-')}.md`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // Toggle section expansion
  const toggleSection = (sectionId) => {
    const newExpanded = new Set(expandedSections)
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId)
    } else {
      newExpanded.add(sectionId)
    }
    setExpandedSections(newExpanded)
  }

  return (
    <div className="min-h-screen bg-bg-primary overflow-x-hidden">
      <style>{`
        .reference-item a {
          color: #60A5FA;
          text-decoration: underline;
          word-break: break-all;
        }
        .reference-item a:hover {
          color: #93C5FD;
        }
        .reference-item em {
          font-style: italic;
          color: #9CA3AF;
        }
        .reference-item {
          list-style: none;
        }
        .reference-item::marker {
          display: none;
        }
      `}</style>
      {/* Header */}
      <AgentHeader
        onBack={onBack}
        icon={FileText}
        title="Artikel Agent"
        query={query}
        iconColor="text-blue-500"
        iconBgColor="bg-blue-500/10"
        actions={isCompleted ? [
          ...(Object.keys(generatedContent).length > 0 ? [{
            onClick: () => setShowAllContent(!showAllContent),
            icon: showAllContent ? EyeOff : Eye,
            label: showAllContent ? 'Sembunyikan Semua' : 'Tampilkan Semua',
            mobileLabel: showAllContent ? 'Sembunyikan' : 'Tampilkan',
            hideOnMobile: false,
            className: cn(buttonVariants.secondary, "flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm")
          }] : []),
          {
            onClick: handleCopy,
            icon: Copy,
            label: copyStatus === 'success' ? 'Tersalin!' : copyStatus === 'error' ? 'Gagal' : 'Salin',
            hideOnMobile: true,
            className: cn(
              buttonVariants.secondary,
              "flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm",
              copyStatus === 'success' && "bg-emerald-500/10 text-emerald-400",
              copyStatus === 'error' && "bg-red-500/10 text-red-400"
            )
          },
          {
             onClick: handleDownload,
             icon: Download,
             label: 'Download',
             hideOnMobile: true,
             className: cn(buttonVariants.primary, "flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm")
           }
         ] : []}
       />

      <div className="max-w-6xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
        {/* Progress Section */}
        {isLoading && (
          <div className="bg-bg-secondary rounded-xl p-4 sm:p-6 mb-6 sm:mb-8 border border-border-primary">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h2 className="text-base sm:text-lg font-semibold text-text-primary">Membuat Artikel Lengkap</h2>
              <div className="flex items-center space-x-1 sm:space-x-2 text-text-tertiary">
                <Clock className="w-4 h-4" />
                <span className="text-xs sm:text-sm">{timeRemaining}</span>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-text-secondary">Sedang menggenerate seluruh artikel...</span>
                <span className="text-text-primary font-medium">{progressPercentage}%</span>
              </div>
              <div className="w-full bg-bg-tertiary rounded-full h-2">
                <div 
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Sources Section - Moved to top after progress */}
        {academicSources.length > 0 && (
          <div className="mb-6 sm:mb-8">
            <h2 className="text-lg sm:text-xl font-semibold text-text-primary mb-4 sm:mb-6">Sumber Referensi</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {academicSources.slice(0, 12).map((source, index) => (
                <SourceCard key={index} source={source} index={index} />
              ))}
            </div>
          </div>
        )}

        {/* Error Section */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-400 flex-shrink-0" />
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-red-400 mb-1">Terjadi Kesalahan</h3>
                <p className="text-sm sm:text-base text-red-300">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Completion Status */}
        {isCompleted && (
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400 flex-shrink-0" />
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-emerald-400 mb-1">Artikel Selesai!</h3>
                <p className="text-sm sm:text-base text-emerald-300">Artikel dengan {wordStats.totalWords} kata telah berhasil dibuat.</p>
              </div>
            </div>
          </div>
        )}

        {/* Statistics */}
        {wordStats.totalWords > 0 && (
          <div className="bg-bg-secondary rounded-xl p-4 sm:p-6 mb-6 sm:mb-8 border border-border-primary">
            <h3 className="text-base sm:text-lg font-semibold text-text-primary mb-3 sm:mb-4">Statistik Artikel</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-blue-500">{wordStats.totalWords}</div>
                <div className="text-xs sm:text-sm text-text-tertiary">Total Kata</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-emerald-500">{outline.length}</div>
                <div className="text-xs sm:text-sm text-text-tertiary">Bagian</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-purple-500">{academicSources.length}</div>
                <div className="text-xs sm:text-sm text-text-tertiary">Sumber</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-orange-500">{allCitations.length}</div>
                <div className="text-xs sm:text-sm text-text-tertiary">Sitasi</div>
              </div>
            </div>
          </div>
        )}

        {/* Content Sections */}
        <div className="space-y-6">
          {outline.filter(section => section.id !== 'referensi').map((section, index) => {
            const content = generatedContent[section.id]
            const isExpanded = expandedSections.has(section.id)
            const hasContent = content && content.trim().length > 0
            const wordCount = hasContent ? countWords(content) : 0
            
            return (
              <div key={section.id} className="bg-bg-secondary rounded-xl border border-border-primary overflow-hidden">
                <div 
                  className="p-6 cursor-pointer hover:bg-bg-tertiary/50 transition-colors"
                  onClick={() => hasContent && toggleSection(section.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <span className="text-sm font-medium text-text-tertiary bg-bg-tertiary px-2 py-1 rounded">
                        #{index + 1}
                      </span>
                      <div>
                        <h3 className="text-lg font-semibold text-text-primary">{section.title}</h3>
                        {section.description && (
                          <p className="text-sm text-text-tertiary mt-1">{section.description}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      {hasContent && (
                        <div className="text-sm text-text-tertiary">
                          {wordCount} kata
                        </div>
                      )}
                      
                      {!hasContent && isLoading && (
                        <Loader className="w-5 h-5 text-blue-500 animate-spin" />
                      )}
                      
                      {hasContent && (
                        <button className="p-1 text-text-tertiary hover:text-text-primary transition-colors">
                          {isExpanded ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      )}
                      
                      {hasContent && (
                        <CheckCircle className="w-5 h-5 text-emerald-500" />
                      )}
                    </div>
                  </div>
                </div>
                
                {hasContent && (isExpanded || showAllContent) && (
                  <div className="border-t border-border-secondary p-6">
                    <MarkdownContent content={content} />
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* References Section */}
        {isCompleted && <ReferencesSection academicSources={academicSources} />}

        {/* Key Takeaways Section */}
        {isCompleted && <KeyTakeawaysSection content={generatedContent} query={query} outline={outline} />}

        {/* Meta Description Section */}
        {isCompleted && <MetaDescriptionSection query={query} wordCount={wordStats.totalWords} outline={outline} content={generatedContent} />}

      </div>
    </div>
  )
}

ArtikelResultPage.propTypes = {
  query: PropTypes.string.isRequired,
  outline: PropTypes.array.isRequired,
  generatedContent: PropTypes.object.isRequired,
  academicSources: PropTypes.array,
  progress: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  isCompleted: PropTypes.bool.isRequired,
  onBack: PropTypes.func.isRequired,
  onBackToSearch: PropTypes.func.isRequired
}

export default ArtikelResultPage